
import { Vue, Options } from "vue-class-component";
import OverlayPanel from "primevue/overlaypanel";
import UpdateDeliveryFormDeleteStt from "@/app/ui/views/incoming-process/update-delivery/update-delivery-form/update-delivery-form-delete-stt.vue";
import { UpdateDeliveryController } from "@/app/ui/controllers/UpdateDeliveryController";
import { ellipsisString, formatPrice } from "@/app/infrastructures/misc/Utils";
import { SttDetail } from "@/domain/entities/UpdateDelivery";

@Options({
  emits: ["process"],
  components: {
    OverlayPanel,
    UpdateDeliveryFormDeleteStt
  }
})
export default class UpdateDeliveryFormTableStt extends Vue {
  pagination = {
    page: 1,
    limit: 50
  };
  // panel button process
  togglePanel(event: any) {
    const refs: any = this.$refs;
    refs.op.toggle(event);
  }
  get form() {
    return UpdateDeliveryController.form;
  }
  get isFormValid() {
    return (
      this.form.deliveredBy.id &&
      this.form.deliveredBy.type &&
      this.form.courier.driverId
    );
  }

  // get data needed
  get listStt() {
    return UpdateDeliveryController.form.listSttNoToUpdate;
  }
  get totalStt() {
    return this.listStt.length;
  }
  get calculatedStt() {
    return this.listStt.reduce(
      (a: any, b: any) => {
        return {
          totalPieces: a.totalPieces + b.sttPieces.length,
          totalGrossWeight: a.totalGrossWeight + b.sttGrossWeight,
          totalVolumeWeight: a.totalVolumeWeight + b.sttVolumeWeight
        };
      },
      {
        totalPieces: 0,
        totalGrossWeight: 0,
        totalVolumeWeight: 0
      }
    );
  }
  get totalSttPieces() {
    return this.calculatedStt.totalPieces;
  }
  get totalSttGrossWeight() {
    return this.formatDecimal(this.calculatedStt.totalGrossWeight);
  }
  get totalSttVolumeWeight() {
    return this.formatDecimal(this.calculatedStt.totalVolumeWeight);
  }
  formatDecimal(val: number) {
    return val.toLocaleString("id", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  // table
  get columns() {
    return [
      {
        name: this.$t("updateDelivery.form.no") as string,
        styleHead: "w-12 text-center whitespace-no-wrap",
        render: (item: SttDetail, index: number) => {
          return `<div class="text-black-lp-300">${index +
            1 +
            this.pagination.limit * (this.pagination.page - 1)}</div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.noSttCol") as string,
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `
          <div class="text-black-lp-300 flex flex-col">
            ${item.sttNo}
            ${
              item.sttNeedToRelabel
                ? `<div class="relabel-chips">
              ${this.$t("updateDelivery.form.needRelabel")}
            </div>`
                : ""
            }
            ${
              item.sttIsFlaggedForPriorityRts
                ? `<div class="flex flex-row gap-1">
                <img src="${require('@/app/ui/assets/svg/information-circle-yellow.svg')}" class="w-16px h-16px" /> <p class="text-14px text-yellow-lp-100">Segera RTS</p>
                </div>`
                : ""
            }
          </div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.noRef")  as string,
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300">${item.sttNoRefExternal || "-"}</div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.deliveryTo") as string,
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300">${item.sttDeliveryAttempt}</div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.codFee") as string,
        styleHead: "w-28 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300">${
            item.sttCodAmount ? formatPrice(item.sttCodAmount) : "-"
          }</div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.dest") as string,
        styleHead: "w-20 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300 flex">
                    <div class="rounded px-2 py-0 bg-gray-lp-400">
                        ${item.sttDestinationCityCode}
                    </div>
                  </div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.product") as string,
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300 flex">
                    <div class="rounded px-2 py-0 bg-gray-lp-400">
                        ${item.sttProductType}
                    </div>
                  </div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.commodity") as string,
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300">${item.sttCommodityName}</div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.koli") as string,
        styleHead: "w-20 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300 flex">
                    ${item.sttTotalPiece}
                  </div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.grossWeight") as string,
        styleHead: "w-36 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300">${this.formatDecimal(
            item.sttGrossWeight
          )} Kg</div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.volumeWeight") as string,
        styleHead: "w-36 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300">${this.formatDecimal(
            item.sttVolumeWeight
          )} Kg</div>`;
        }
      },
      {
        name: this.$t("updateDelivery.form.chargeableWeight") as string,
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: SttDetail) => {
          return `<div class="text-black-lp-300">${this.formatDecimal(
            item.sttChargeAbleWeight
          )} Kg</div>`;
        }
      }
    ]
  };

  // dialog delete stt
  openDeleteStt = false;
  onCloseDeleteStt() {
    this.openDeleteStt = false;
  }

  // on process del
  onProcessDel(withPrint: boolean) {
    const refs: any = this.$refs;
    refs.op.hide();
    this.$emit("process", withPrint);
  }
}
